/**
 * CubeKernel：精细 3D 魔方模型 + 层转动引擎（框架无关，R3F 通过 <primitive> 挂载）
 *
 * 模型规范：26 cubie（RoundedBox 倒角 + 分模线缝隙）+ 半透明机械核心；
 * 贴纸 metalness 0.1 / roughness 0.4；黑体 ABS 质感；转层拖尾光带。
 *
 * 引擎语义自旧版 cubeMesh.js / animator.js 逐行平移（已验证，禁止重写）：
 *  - FACELET_MAP 贴纸映射、FACE_TABLE 轴/方向、层收集、90° snap
 *  - animateMove 的同步收尾（finishNow）防重入
 *  - Animator 的 load/next/prev/goTo/play/pause/stop + _gen 代数守卫 + busy 防抖
 */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three-stdlib';
import { FACE_COLOR_HEX, type Face } from '../core/cube';
import { parseMove, isInLayer, inverseToken, moveAngle, type Move } from '../core/notation';
import { FACELET_MAP, FACE_NORMAL } from './faceletMap';

const CUBIE_SIZE = 0.965; // 留 0.035 缝隙 = 分模线
const STEP = 1.0;
const BEVEL = 0.05; // 倒角
const STICKER_SIZE = 0.8;
const STICKER_RADIUS = 0.1;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** 圆角方形贴纸 geometry（共享一份） */
function makeStickerGeometry(): THREE.ShapeGeometry {
  const s = STICKER_SIZE / 2;
  const r = STICKER_RADIUS;
  const shape = new THREE.Shape();
  shape.moveTo(-s + r, -s);
  shape.lineTo(s - r, -s);
  shape.quadraticCurveTo(s, -s, s, -s + r);
  shape.lineTo(s, s - r);
  shape.quadraticCurveTo(s, s, s - r, s);
  shape.lineTo(-s + r, s);
  shape.quadraticCurveTo(-s, s, -s, s - r);
  shape.lineTo(-s, -s + r);
  shape.quadraticCurveTo(-s, -s, -s + r, -s);
  return new THREE.ShapeGeometry(shape, 6);
}

interface MoveController {
  cancelled: boolean;
  promise: Promise<void> | null;
  finishNow: () => void;
}

export class CubeKernel {
  root = new THREE.Group();
  cubies: THREE.Group[] = [];
  stickersByFacelet: (THREE.Mesh | null)[] = new Array(54).fill(null);
  trailEnabled = true;

  private bodyGeo: RoundedBoxGeometry;
  private stickerGeo: THREE.ShapeGeometry;
  private bodyMat: THREE.MeshPhysicalMaterial;
  private coreParts: (THREE.Mesh | THREE.LineSegments)[] = [];
  private _active: MoveController | null = null;
  private _trailMesh: THREE.Mesh | null = null;
  private _disposed = false;

  constructor() {
    this.bodyGeo = new RoundedBoxGeometry(CUBIE_SIZE, CUBIE_SIZE, CUBIE_SIZE, 4, BEVEL);
    this.stickerGeo = makeStickerGeometry();
    this.bodyMat = new THREE.MeshPhysicalMaterial({
      color: 0x0d0d12,
      roughness: 0.6,
      metalness: 0.05,
      clearcoat: 0.3,
      clearcoatRoughness: 0.5,
    });
    this._build();
    this._buildCore();
  }

  private _build() {
    for (let x = -1; x <= 1; x++) {
      for (let y = -1; y <= 1; y++) {
        for (let z = -1; z <= 1; z++) {
          if (x === 0 && y === 0 && z === 0) continue; // 中心留给机械核心
          const cubie = new THREE.Group();
          cubie.position.set(x * STEP, y * STEP, z * STEP);
          cubie.userData = { logic: [x, y, z] };

          const body = new THREE.Mesh(this.bodyGeo, this.bodyMat);
          cubie.add(body);

          for (let fi = 0; fi < 54; fi++) {
            const info = FACELET_MAP[fi];
            if (info.pos[0] === x && info.pos[1] === y && info.pos[2] === z) {
              const sticker = this._makeSticker(info.face);
              cubie.add(sticker);
              this.stickersByFacelet[fi] = sticker;
            }
          }

          this.cubies.push(cubie);
          this.root.add(cubie);
        }
      }
    }
  }

  private _makeSticker(face: Face): THREE.Mesh {
    const color = FACE_COLOR_HEX[face];
    const mat = new THREE.MeshStandardMaterial({
      color,
      metalness: 0.1,
      roughness: 0.4,
      emissive: color,
      emissiveIntensity: 0.06,
    });
    const mesh = new THREE.Mesh(this.stickerGeo, mat);
    const n = FACE_NORMAL[face];
    mesh.position.copy(n).multiplyScalar(CUBIE_SIZE / 2 + 0.004);
    mesh.lookAt(mesh.position.clone().add(n));
    return mesh;
  }

  /** 半透明核心：球体 + 线框二十面体 + 三轴环，"精密机械结构若隐若现" */
  private _buildCore() {
    const core = new THREE.Group();

    const shellGeo = new THREE.SphereGeometry(0.6, 32, 24);
    const shellMat = new THREE.MeshPhysicalMaterial({
      color: 0x8ad4ff,
      metalness: 0.7,
      roughness: 0.2,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
    });
    const shell = new THREE.Mesh(shellGeo, shellMat);
    core.add(shell);
    this.coreParts.push(shell);

    const icoGeo = new THREE.IcosahedronGeometry(0.42, 0);
    const icoEdges = new THREE.EdgesGeometry(icoGeo);
    icoGeo.dispose();
    const icoMat = new THREE.LineBasicMaterial({
      color: 0x00e5ff,
      transparent: true,
      opacity: 0.5,
    });
    const ico = new THREE.LineSegments(icoEdges, icoMat);
    core.add(ico);
    this.coreParts.push(ico);

    const ringGeo = new THREE.TorusGeometry(0.52, 0.02, 8, 40);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x3a3f4d,
      metalness: 0.9,
      roughness: 0.3,
      transparent: true,
      opacity: 0.65,
    });
    const axes: [number, number, number][] = [
      [Math.PI / 2, 0, 0],
      [0, Math.PI / 2, 0],
      [0, 0, 0],
    ];
    for (const rot of axes) {
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.set(rot[0], rot[1], rot[2]);
      core.add(ring);
      this.coreParts.push(ring);
    }

    this.root.add(core);
  }

  /** 用 54 字符 facelet 刷新所有贴纸颜色 */
  applyFacelet(facelet: string) {
    for (let i = 0; i < 54; i++) {
      const sticker = this.stickersByFacelet[i];
      const ch = facelet[i] as Face;
      const hex = FACE_COLOR_HEX[ch];
      if (sticker && hex !== undefined) {
        const mat = sticker.material as THREE.MeshStandardMaterial;
        mat.color.setHex(hex);
        mat.emissive.setHex(hex);
      }
    }
  }

  /** 复位所有 cubie 到出厂 transform（配合 applyFacelet 显示任意状态） */
  reset() {
    this.finishActive();
    let i = 0;
    for (let x = -1; x <= 1; x++) {
      for (let y = -1; y <= 1; y++) {
        for (let z = -1; z <= 1; z++) {
          if (x === 0 && y === 0 && z === 0) continue;
          const cubie = this.cubies[i++];
          cubie.position.set(x * STEP, y * STEP, z * STEP);
          cubie.rotation.set(0, 0, 0);
          cubie.userData.logic = [x, y, z];
        }
      }
    }
  }

  /** 若有未收尾的层动画，立刻同步烘焙到终点 */
  finishActive() {
    if (this._active) {
      this._active.cancelled = true;
      this._active.finishNow();
    }
  }

  private _syncLogicAfterTurn() {
    for (const cubie of this.cubies) {
      const p = cubie.position;
      cubie.userData.logic = [
        Math.round(p.x / STEP),
        Math.round(p.y / STEP),
        Math.round(p.z / STEP),
      ];
    }
  }

  private _collectLayer(move: Move) {
    return this.cubies.filter((c) =>
      isInLayer(move, c.userData.logic as [number, number, number])
    );
  }

  /** 同步、无动画应用一步转动 */
  applyMoveInstant(token: string) {
    const move = parseMove(token);
    if (!move) throw new Error('非法记法：' + token);
    this.finishActive();
    const layer = this._collectLayer(move);
    if (layer.length === 0) return;

    const pivot = new THREE.Group();
    this.root.add(pivot);
    for (const c of layer) pivot.attach(c);

    const axisVec = new THREE.Vector3(
      move.axis === 'x' ? 1 : 0,
      move.axis === 'y' ? 1 : 0,
      move.axis === 'z' ? 1 : 0
    );
    pivot.setRotationFromAxisAngle(axisVec, moveAngle(move));

    for (const c of [...pivot.children]) this.root.attach(c);
    this.root.remove(pivot);

    for (const c of layer) this._snap(c);
    this._syncLogicAfterTurn();
  }

  /** 拖尾光带：转层期间在该层平面绘制发光弧带 */
  private _updateTrail(move: Move, sweep: number, t: number) {
    if (!this.trailEnabled) return;
    this._removeTrail();
    const abs = Math.abs(sweep);
    if (abs < 0.02) return;

    const geo = new THREE.RingGeometry(1.58, 1.76, 40, 1, 0, abs);
    const mat = new THREE.MeshBasicMaterial({
      color: FACE_COLOR_HEX[move.face],
      transparent: true,
      opacity: 0.38 * Math.sin(Math.PI * Math.min(1, t)),
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);

    // 环默认躺在 XY 平面（法线 +Z），旋转到与转动轴垂直，平移到该层
    if (move.axis === 'x') {
      mesh.rotation.y = Math.PI / 2;
      mesh.position.x = move.layer * STEP;
    } else if (move.axis === 'y') {
      mesh.rotation.x = Math.PI / 2;
      mesh.position.y = move.layer * STEP;
    } else {
      mesh.position.z = move.layer * STEP;
    }
    // 让弧带随转动方向扫过
    if (sweep < 0) mesh.rotation.z = sweep;

    this._trailMesh = mesh;
    this.root.add(mesh);
  }

  private _removeTrail() {
    if (this._trailMesh) {
      this.root.remove(this._trailMesh);
      this._trailMesh.geometry.dispose();
      (this._trailMesh.material as THREE.Material).dispose();
      this._trailMesh = null;
    }
  }

  /** 执行一步动画（easeInOutCubic），返回带 finishNow 的 controller */
  animateMove(token: string, opts: { duration?: number } = {}): MoveController {
    const move = parseMove(token);
    if (!move) throw new Error('非法记法：' + token);
    this.finishActive();
    const duration = Math.max(1, opts.duration ?? 300);
    const axisVec = new THREE.Vector3(
      move.axis === 'x' ? 1 : 0,
      move.axis === 'y' ? 1 : 0,
      move.axis === 'z' ? 1 : 0
    );
    const targetAngle = moveAngle(move);
    const layer = this._collectLayer(move);

    const pivot = new THREE.Group();
    this.root.add(pivot);
    for (const c of layer) pivot.attach(c);

    const controller: MoveController = { cancelled: false, promise: null, finishNow: () => {} };
    const start = performance.now();
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      pivot.setRotationFromAxisAngle(axisVec, targetAngle);
      for (const c of [...pivot.children]) this.root.attach(c);
      this.root.remove(pivot);
      for (const c of layer) this._snap(c);
      this._syncLogicAfterTurn();
      this._removeTrail();
      if (this._active === controller) this._active = null;
    };
    controller.finishNow = finish;
    this._active = controller;

    controller.promise = new Promise<void>((resolve) => {
      const tick = () => {
        if (this._disposed) {
          resolve();
          return;
        }
        if (finished) {
          resolve();
          return;
        }
        if (controller.cancelled) {
          finish();
          resolve();
          return;
        }
        const t = Math.min(1, (performance.now() - start) / duration);
        const eased = easeInOutCubic(t);
        const angle = targetAngle * eased;
        pivot.setRotationFromAxisAngle(axisVec, angle);
        this._updateTrail(move, angle, t);
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          finish();
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });

    return controller;
  }

  private _snap(c: THREE.Object3D) {
    c.position.set(
      Math.round(c.position.x / STEP) * STEP,
      Math.round(c.position.y / STEP) * STEP,
      Math.round(c.position.z / STEP) * STEP
    );
    const snap = (v: number) => Math.round(v / (Math.PI / 2)) * (Math.PI / 2);
    c.rotation.set(snap(c.rotation.x), snap(c.rotation.y), snap(c.rotation.z));
  }

  dispose() {
    this._disposed = true;
    this.finishActive();
    this._removeTrail();
    this.bodyGeo.dispose();
    this.stickerGeo.dispose();
    this.bodyMat.dispose();
    for (const s of this.stickersByFacelet) {
      if (s) (s.material as THREE.Material).dispose();
    }
    for (const p of this.coreParts) {
      p.geometry.dispose();
      (p.material as THREE.Material).dispose();
    }
  }
}

/* ================= 播放队列（自 animator.js 平移） ================= */

export class AnimatorKernel {
  moves: string[] = [];
  cursor = 0;
  playing = false;
  speed = 1;
  duration = 460;
  onStep: ((cursor: number, token: string) => void) | null = null;
  onEnd: (() => void) | null = null;

  private kernel: CubeKernel;
  private _currentController: MoveController | null = null;
  private _busy = false;
  private _gen = 0;

  constructor(kernel: CubeKernel) {
    this.kernel = kernel;
  }

  load(moves: string[]) {
    this.stop();
    this.moves = moves.slice();
    this.cursor = 0;
  }

  get total() {
    return this.moves.length;
  }

  async next(): Promise<boolean> {
    if (this._busy) return false;
    if (this.cursor >= this.moves.length) return false;
    this._busy = true;
    try {
      const gen = this._gen;
      const token = this.moves[this.cursor];
      await this._run(token);
      if (this._gen !== gen) return false;
      this.cursor++;
      this.onStep?.(this.cursor, token);
      if (this.cursor >= this.moves.length) {
        this.playing = false;
        this.onEnd?.();
      }
      return true;
    } finally {
      this._busy = false;
    }
  }

  async prev(): Promise<boolean> {
    if (this._busy) return false;
    if (this.cursor <= 0) return false;
    this._busy = true;
    try {
      const gen = this._gen;
      this.cursor--;
      const token = this.moves[this.cursor];
      await this._run(inverseToken(token));
      if (this._gen !== gen) return false;
      this.onStep?.(this.cursor, token);
      return true;
    } finally {
      this._busy = false;
    }
  }

  async goTo(n: number) {
    n = Math.max(0, Math.min(this.moves.length, n));
    while (this.cursor > n) {
      if (!(await this.prev())) break;
    }
    while (this.cursor < n) {
      if (!(await this.next())) break;
    }
  }

  async play() {
    if (this.playing) return;
    this.playing = true;
    while (this.playing && this.cursor < this.moves.length) {
      await this.next();
      if (!this.playing) break;
      await delay(60 / this.speed);
    }
    this.playing = false;
  }

  pause() {
    this.playing = false;
  }

  stop() {
    this.playing = false;
    this._gen++;
    if (this._currentController) {
      this._currentController.cancelled = true;
      this._currentController.finishNow();
      this._currentController = null;
    }
  }

  private async _run(token: string) {
    const dur = this.duration / this.speed;
    this._currentController = this.kernel.animateMove(token, { duration: dur });
    await this._currentController.promise;
    this._currentController = null;
  }
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
